import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, ShoppingCart, Building2, Heart, Briefcase, Quote } from "lucide-react";
import SEO from "@/components/SEO";

const CASOS = [
  {
    sector: "E-commerce",
    icon: ShoppingCart,
    color: "blue",
    titulo: "Tienda online con pixels de marketing",
    perfil: "Marketplace nicho con 12.000 órdenes/mes. Usa Meta Pixel, TikTok Pixel y Google Ads.",
    problema:
      "Antes del scan, cargaba los pixels apenas el usuario entraba al sitio — sin consentimiento previo. La APDP considera esto incumplimiento del Art. 12 (consentimiento informado) en checkout.",
    accion: [
      "Deep Scan detectó 4 trackers en /checkout antes del banner.",
      "Generó Política de Privacidad (12 ítems Art. 14 ter) + Cláusula de Consentimiento para checkout.",
      "Implementó banner de cookies con bloqueo previo de pixels.",
    ],
    resultado: { score_antes: 48, score_despues: 89, riesgo: "Grave → Bajo", multa_evitada: "$68M – $688M" },
  },
  {
    sector: "Clínica",
    icon: Heart,
    color: "rose",
    titulo: "Clínica con formularios médicos online",
    perfil: "Red de centros médicos · agendamiento online + chatbot IA para síntomas.",
    problema:
      "Datos sensibles de salud (Art. 2 letra g) sin base legal documentada. Chatbot procesa síntomas sin declarar decisiones automatizadas (Art. 14 ter literal 12).",
    accion: [
      "Catálogo de Datos Personales (RAT) detallando cada formulario.",
      "Política de Privacidad declarando explícitamente bases legales para datos de salud.",
      "Cláusula específica de decisiones automatizadas del chatbot con derecho a oposición.",
    ],
    resultado: { score_antes: 35, score_despues: 92, riesgo: "Gravísima → Medio", multa_evitada: "$138M – $1.4 mil M" },
  },
  {
    sector: "Agencia digital",
    icon: Briefcase,
    color: "violet",
    titulo: "Agencia que gestiona 28 sitios de clientes",
    perfil: "Agencia de marketing digital con 28 cuentas activas. Cobra mantenimiento + SEO.",
    problema:
      "Cada cliente exige saber si su sitio cumple la Ley 21.719. Auditar manualmente cada uno tomaba ~6h por cliente. Costo prohibitivo.",
    accion: [
      "Activaron Pro y escanean los 28 sitios cada lunes con Deep Scan.",
      "Cada cliente recibe reporte mensual con score, hallazgos y plan de acción.",
      "Cuando un cliente exige documentos, los generan en 8 minutos vs días de abogado externo.",
    ],
    resultado: { score_antes: 0, score_despues: 0, riesgo: "Servicio nuevo → +$240k/mes", multa_evitada: "—" },
  },
  {
    sector: "ONG",
    icon: Heart,
    color: "emerald",
    titulo: "Fundación con base de donantes",
    perfil: "ONG con 14.000 donantes activos. Newsletter mensual + campañas estacionales.",
    problema:
      "Tenía consentimientos ambiguos de 2015–2023, sin posibilidad de demostrar la fuente del dato (literal 10 Art. 14 ter) ni derecho a retirar consentimiento visible.",
    accion: [
      "Política de Privacidad re-emitida citando fuente de datos por categoría.",
      "Botón visible de retirar consentimiento en cada email + página de preferencias.",
      "Catálogo de Datos Personales para responder fiscalizaciones de la APDP.",
    ],
    resultado: { score_antes: 56, score_despues: 94, riesgo: "Grave → Bajo", multa_evitada: "$34M – $344M" },
  },
  {
    sector: "PYME servicios",
    icon: Building2,
    color: "amber",
    titulo: "Estudio contable con formulario de contacto",
    perfil: "Estudio contable PYME — 7 personas. Web con formulario de contacto + WhatsApp.",
    problema:
      "Formulario sin checkbox de consentimiento. Sin política de privacidad. Sin canal ARCO declarado. Tipo más común que vemos en Chile.",
    accion: [
      "Política de Privacidad Art. 14 ter (12 ítems).",
      "Cláusula de Consentimiento para el formulario.",
      "Email privacidad@... configurado + texto del canal ARCO en el footer.",
    ],
    resultado: { score_antes: 22, score_despues: 96, riesgo: "Grave → Bajo", multa_evitada: "PYME 1ra vez: Amonestación" },
  },
];

const TONES = {
  blue: { box: "border-blue-200 bg-blue-50", iconBox: "bg-blue-100 text-blue-700", chip: "bg-blue-600 text-white" },
  rose: { box: "border-rose-200 bg-rose-50", iconBox: "bg-rose-100 text-rose-700", chip: "bg-rose-600 text-white" },
  violet: { box: "border-violet-200 bg-violet-50", iconBox: "bg-violet-100 text-violet-700", chip: "bg-violet-600 text-white" },
  emerald: { box: "border-emerald-200 bg-emerald-50", iconBox: "bg-emerald-100 text-emerald-700", chip: "bg-emerald-600 text-white" },
  amber: { box: "border-amber-200 bg-amber-50", iconBox: "bg-amber-100 text-amber-700", chip: "bg-amber-600 text-white" },
};

export default function Casos() {
  return (
    <>
      <SEO
        title="Casos de uso"
        description="5 casos reales de empresas chilenas que cumplen la Ley 21.719 con Ley de Datos Chile: e-commerce, clínicas, agencias, ONG y PYMEs."
        path="/casos"
      />

      {/* HERO */}
      <section className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-7">
            <span className="inline-block w-6 h-px bg-blue-400/60" />
            Casos · Antes / Después
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight max-w-4xl">
            Cómo PYMEs y agencias chilenas pasaron de <em className="italic text-rose-300">riesgo</em> a <em className="italic text-blue-300">cumplimiento</em>.
          </h1>
          <p className="mt-8 text-lg text-slate-300 leading-relaxed max-w-3xl">
            Cinco perfiles reales (anonimizados) que muestran qué encuentra el escáner, qué documentos resuelven el problema y cuánto baja el riesgo medido.
          </p>

          <div className="mt-12 pt-10 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
            {[
              { k: "5", v: "Sectores cubiertos" },
              { k: "+72 pts", v: "Score promedio post-acción" },
              { k: "12 ítems", v: "Art. 14 ter declarados" },
              { k: "8 min", v: "Promedio onboarding → documento" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-display text-2xl sm:text-3xl text-white">{s.k}</div>
                <div className="mt-1 text-[11px] text-slate-400 leading-snug uppercase tracking-wider font-mono">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Casos numerados */}
      <section className="px-6 py-20 sm:py-24">
        <div className="max-w-6xl mx-auto space-y-16">
          {CASOS.map((c, idx) => {
            const tone = TONES[c.color];
            const Icon = c.icon;
            const n = String(idx + 1).padStart(2, "0");
            return (
              <article key={c.sector} data-testid={`caso-${idx + 1}`} className="grid lg:grid-cols-12 gap-8 lg:gap-12">
                {/* Left meta */}
                <div className="lg:col-span-4">
                  <div className="sticky top-24">
                    <span className="font-mono text-xs text-blue-700 tracking-wider">{n}</span>
                    <span className={`ml-3 inline-block px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${tone.chip}`}>
                      {c.sector}
                    </span>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl text-slate-900 leading-tight tracking-tight">
                      {c.titulo}
                    </h2>
                    <p className="mt-4 text-sm text-slate-600 leading-relaxed">{c.perfil}</p>

                    <div className={`mt-6 inline-flex items-center justify-center w-12 h-12 rounded-2xl ${tone.iconBox}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                </div>

                {/* Right detail */}
                <div className="lg:col-span-8 space-y-5">
                  <div className={`rounded-2xl border ${tone.box} p-6`}>
                    <div className="flex items-start gap-3">
                      <Quote className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
                      <div>
                        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">El problema</p>
                        <p className="text-slate-800 leading-relaxed">{c.problema}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-2xl p-6">
                    <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-blue-700 mb-3">Qué hicimos</p>
                    <ul className="space-y-2.5">
                      {c.accion.map((a, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed">
                          <span className="font-mono text-[10px] text-blue-700 mt-1.5 tracking-wider">{String(i + 1).padStart(2, "0")}</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Resultado: tira métricas */}
                  <div className="bg-ink-950 text-white rounded-2xl p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" />
                    <div className="relative">
                      <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-blue-300 mb-4">Resultado</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {c.resultado.score_antes > 0 && (
                          <div>
                            <p className="font-mono text-[9px] text-slate-500 uppercase tracking-wider mb-1">Antes</p>
                            <p className="font-display text-2xl text-rose-300 tabular-nums">{c.resultado.score_antes}</p>
                          </div>
                        )}
                        {c.resultado.score_despues > 0 && (
                          <div>
                            <p className="font-mono text-[9px] text-slate-500 uppercase tracking-wider mb-1">Después</p>
                            <p className="font-display text-2xl text-emerald-300 tabular-nums">{c.resultado.score_despues}</p>
                          </div>
                        )}
                        <div>
                          <p className="font-mono text-[9px] text-slate-500 uppercase tracking-wider mb-1">Riesgo APDP</p>
                          <p className="text-sm text-slate-200">{c.resultado.riesgo}</p>
                        </div>
                        <div>
                          <p className="font-mono text-[9px] text-slate-500 uppercase tracking-wider mb-1">Multa evitada</p>
                          <p className="text-sm text-slate-200">{c.resultado.multa_evitada}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-20 sm:py-24 text-center">
          <ShieldCheck className="w-10 h-10 text-blue-700 mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight max-w-2xl mx-auto">
            ¿En qué situación estás <em className="italic text-blue-700">tú</em>?
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-xl mx-auto">
            Escaneamos tu sitio en 30 segundos y te decimos exactamente qué arreglar primero.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/"
              data-testid="casos-cta-escaner"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors"
            >
              Escanear mi sitio gratis <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/modulo-pro"
              data-testid="casos-cta-pro"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-300 hover:bg-white text-slate-700 font-semibold transition-colors"
            >
              Ver Pro · $14.990
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
